import React, { useContext, useState, useEffect, useCallback } from 'react';
import { AssistantContext, DEFAULT_LLMS } from '../../context/AssistantContext';
import { FiUser, FiMessageSquare, FiDatabase, FiCommand, FiX, FiSettings, FiLogOut, FiCpu, FiEdit2, FiTrash2, FiCheck, FiXCircle, FiPlus } from 'react-icons/fi';
import './Settings.css';
import CustomSelect from './CustomSelect';
import AlertBox from '../alert-box/AlertBox';
import { clearAuth, authFetch, redirectToLogin } from '../../utils/auth';
import { fetchMemories, createMemory, updateMemory, deleteMemory, deleteAllMemories } from '../../api/memoriesApi';

const Settings = ({ isOpen, onClose }) => {
  const { settings, updateSettings, loading, error, refreshSettings } = useContext(AssistantContext);
  const [activeTab, setActiveTab] = useState('ui');
  const [tempSettings, setTempSettings] = useState(settings);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Memories state
  const [memories, setMemories] = useState([]);
  const [memoriesTotal, setMemoriesTotal] = useState(0);
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [memoriesError, setMemoriesError] = useState(null);
  const [memoriesCategory, setMemoriesCategory] = useState('');
  const [memoriesOffset, setMemoriesOffset] = useState(0);
  const [editingMemoryId, setEditingMemoryId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [editingCategory, setEditingCategory] = useState('');
  const [editingImportance, setEditingImportance] = useState('');
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [memoryActionLoading, setMemoryActionLoading] = useState(null); // holds memory id during action
  const [showAddMemoryForm, setShowAddMemoryForm] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState('');
  const [newMemoryCategory, setNewMemoryCategory] = useState('');
  const [newMemoryImportance, setNewMemoryImportance] = useState('medium');
  const [addMemoryLoading, setAddMemoryLoading] = useState(false);

  const MEMORIES_LIMIT = 20;
  const MEMORY_CATEGORIES = [
    { value: '', label: 'All Categories' },
    { value: 'technical_detail', label: 'Technical Detail' },
    { value: 'job_context', label: 'Job Context' },
    { value: 'customer_context', label: 'Customer Context' },
    { value: 'user_preference', label: 'User Preference' },
    { value: 'user_fact', label: 'User Fact' },
    { value: 'user_decision', label: 'User Decision' },
    { value: 'user_need', label: 'User Need' },
    { value: 'user_goal', label: 'User Goal' },
    { value: 'user_pain_point', label: 'User Pain Point' },
    { value: 'timeline_event', label: 'Timeline Event' },
    { value: 'other', label: 'Other' },
  ];

  const IMPORTANCE_OPTIONS = ['high', 'medium', 'low'];

  const loadMemories = useCallback(async (offset = 0, category = memoriesCategory) => {
    setMemoriesLoading(true);
    setMemoriesError(null);
    try {
      const data = await fetchMemories({ category: category || undefined, limit: MEMORIES_LIMIT, offset });
      setMemories(data.memories || []);
      setMemoriesTotal(data.total || 0);
      setMemoriesOffset(offset);
    } catch (err) {
      console.error('Error loading memories:', err);
      setMemoriesError(err.message || 'Failed to load memories');
    } finally {
      setMemoriesLoading(false);
    }
  }, [memoriesCategory]);

  useEffect(() => {
    // Reset temp settings when the modal opens
    if (isOpen) {
      // Apply defaults while preserving the original structure
      setTempSettings({
        ...settings,
        customPrompts: Array.isArray(settings.customPrompts) ? settings.customPrompts : [],
        dataSources: {
          ...(settings.dataSources || {}),
          enabledSources: Array.isArray(settings.dataSources?.enabledSources) ? 
                         settings.dataSources.enabledSources : []
        }
      });
      
      // When opening, explicitly reset hasChanges to false
      setHasChanges(false);
      setShowUnsavedAlert(false);
      setSaveError(null);
    }
  }, [isOpen, settings]);

  // Add this helper function to deeply compare objects
  const isEqual = (obj1, obj2) => {
    // Handle primitive types
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) return false;
    
    // Convert arrays to objects for comparison if needed
    const o1 = Array.isArray(obj1) ? {...obj1} : obj1;
    const o2 = Array.isArray(obj2) ? {...obj2} : obj2;
    
    // Get all keys from both objects
    const keys1 = Object.keys(o1);
    const keys2 = Object.keys(o2);
    
    // If key counts differ, objects are not equal
    if (keys1.length !== keys2.length) return false;
    
    // Check if every key/value pair is equivalent
    return keys1.every(key => {
      // Recursively compare nested objects
      if (typeof o1[key] === 'object' && o1[key] !== null) {
        return isEqual(o1[key], o2[key]);
      }
      
      // Compare empty strings with undefined/null as equal
      if ((o1[key] === '' && (o2[key] === undefined || o2[key] === null || o2[key] === '')) ||
          (o2[key] === '' && (o1[key] === undefined || o1[key] === null || o1[key] === ''))) {
        return true;
      }
      
      // Direct comparison for other values
      return o1[key] === o2[key];
    });
  };

  useEffect(() => {
    // Only check for changes if modal is open and after initial render
    if (isOpen && tempSettings) {
      try {
        // Normalize both objects for proper comparison
        const normalizedSettings = {
          ...settings,
          theme: settings.theme || 'light',
          customPrompts: Array.isArray(settings.customPrompts) ? settings.customPrompts : [],
          userProfile: {
            preferredName: settings.userProfile?.preferredName || '',
            jobTitle: settings.userProfile?.jobTitle || '',
            organization: settings.userProfile?.organization || '',
            team: settings.userProfile?.team || '',
            preferredLanguage: settings.userProfile?.preferredLanguage || '',
            ...(settings.userProfile || {})
          },
          responseConfig: {
            tone: settings.responseConfig?.tone || 'professional',
            responseLength: settings.responseConfig?.responseLength || 'balanced',
            ...(settings.responseConfig || {})
          },
          dataSources: {
            ...(settings.dataSources || {}),
            enabledSources: Array.isArray(settings.dataSources?.enabledSources) ? 
                           settings.dataSources.enabledSources : []
          }
        };
        
        const normalizedTempSettings = {
          ...tempSettings,
          theme: tempSettings.theme || 'light',
          customPrompts: Array.isArray(tempSettings.customPrompts) ? tempSettings.customPrompts : [],
          userProfile: {
            preferredName: tempSettings.userProfile?.preferredName || '',
            jobTitle: tempSettings.userProfile?.jobTitle || '',
            organization: tempSettings.userProfile?.organization || '',
            team: tempSettings.userProfile?.team || '',
            preferredLanguage: tempSettings.userProfile?.preferredLanguage || '',
            ...(tempSettings.userProfile || {})
          },
          responseConfig: {
            tone: tempSettings.responseConfig?.tone || 'professional',
            responseLength: tempSettings.responseConfig?.responseLength || 'balanced',
            ...(tempSettings.responseConfig || {})
          },
          dataSources: {
            ...(tempSettings.dataSources || {}),
            enabledSources: Array.isArray(tempSettings.dataSources?.enabledSources) ? 
                           tempSettings.dataSources.enabledSources : []
          }
        };
        
        // Use custom deep equality check
        const areEqual = isEqual(normalizedSettings, normalizedTempSettings);
        setHasChanges(!areEqual);
      } catch (err) {
        console.error("Error comparing settings:", err);
        // Default to no changes if there's an error in comparison
        setHasChanges(false);
      }
    }
  }, [isOpen, settings, tempSettings]);

  // Load memories when the tab becomes active
  useEffect(() => {
    if (isOpen && activeTab === 'memories') {
      loadMemories(0, memoriesCategory);
    }
  }, [isOpen, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handleLogout = async () => {
    try {
      await authFetch("/api/logout");
      clearAuth();
      redirectToLogin();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleResponseConfigChange = (key, value) => {
    setTempSettings({
      ...tempSettings,
      responseConfig: {
        ...(tempSettings.responseConfig || {}),
        [key]: value
      }
    });
  };

  const handleUserProfileChange = (key, value) => {
    setTempSettings({
      ...tempSettings,
      userProfile: {
        ...(tempSettings.userProfile || {}),
        [key]: value
      }
    });
  };

  const handleDataSourceToggle = (source) => {
    // Ensure dataSources and its properties exist
    const dataSources = tempSettings.dataSources || {};
    
    // Ensure enabledSources is an array
    const enabledSources = Array.isArray(dataSources.enabledSources) ? 
                          dataSources.enabledSources : [];
    
    // Check if source exists in enabledSources
    const sourceEnabled = dataSources[source]?.enabled || false;
    
    const newEnabledSources = sourceEnabled
      ? enabledSources.filter(s => s !== source)
      : [...enabledSources, source];

    // Create source object if it doesn't exist
    const sourceObj = dataSources[source] || {};

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        [source]: {
          ...sourceObj,
          enabled: !sourceEnabled
        },
        enabledSources: newEnabledSources
      }
    });
  };

  const handleCustomPromptAdd = (prompt) => {
    setTempSettings({
      ...tempSettings,
      customPrompts: Array.isArray(tempSettings.customPrompts) 
        ? [...tempSettings.customPrompts, prompt] 
        : [prompt]
    });
  };

  const handleCustomPromptDelete = (index) => {
    if (!Array.isArray(tempSettings.customPrompts)) {
      return;
    }
    const newPrompts = [...tempSettings.customPrompts];
    newPrompts.splice(index, 1);
    setTempSettings({
      ...tempSettings,
      customPrompts: newPrompts
    });
  };

  const handleSolutionToggle = (source, solution) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const sourceObj = dataSources[source] || {};
    const solutions = sourceObj.solutions || {};
    const solutionObj = solutions[solution] || {};

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        [source]: {
          ...sourceObj,
          solutions: {
            ...solutions,
            [solution]: {
              ...solutionObj,
              enabled: !solutionObj.enabled
            }
          }
        }
      }
    });
  };

  const handleSectionToggle = (source, solution, sectionName) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const sourceObj = dataSources[source] || {};
    const solutions = sourceObj.solutions || {};
    const solutionObj = solutions[solution] || {};
    const sections = solutionObj.sections || [];

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        [source]: {
          ...sourceObj,
          solutions: {
            ...solutions,
            [solution]: {
              ...solutionObj,
              sections: sections.map(section => 
                section.name === sectionName ? { ...section, enabled: !section.enabled } : section
              )
            }
          }
        }
      }
    });
  };

  const handleWorkfrontSectionToggle = (section) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const workfront = dataSources.workfront || {};
    const sections = workfront.sections || {};
    const sectionObj = sections[section] || {};

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        workfront: {
          ...workfront,
          sections: {
            ...sections,
            [section]: {
              ...sectionObj,
              enabled: !sectionObj.enabled
            }
          }
        }
      }
    });
  };

  const handleWorkfrontFilterToggle = (section, filterName) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const workfront = dataSources.workfront || {};
    const sections = workfront.sections || {};
    const sectionObj = sections[section] || {};
    const filters = sectionObj.filters || [];

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        workfront: {
          ...workfront,
          sections: {
            ...sections,
            [section]: {
              ...sectionObj,
              filters: filters.map(filter => 
                filter.name === filterName ? { ...filter, enabled: !filter.enabled } : filter
              )
            }
          }
        }
      }
    });
  };

  const handleSlackCategoryToggle = (category) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const slack = dataSources.slack || {};
    const channels = slack.channels || {};
    const categoryObj = channels[category] || {};

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        slack: {
          ...slack,
          channels: {
            ...channels,
            [category]: {
              ...categoryObj,
              enabled: !categoryObj.enabled
            }
          }
        }
      }
    });
  };

  const handleSlackChannelToggle = (category, channelName) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const slack = dataSources.slack || {};
    const channels = slack.channels || {};
    const categoryObj = channels[category] || {};
    const channelsList = categoryObj.channels || [];

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        slack: {
          ...slack,
          channels: {
            ...channels,
            [category]: {
              ...categoryObj,
              channels: channelsList.map(channel =>
                channel.name === channelName ? { ...channel, enabled: !channel.enabled } : channel
              )
            }
          }
        }
      }
    });
  };

  const handleWikiSpaceToggle = (space) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const wiki = dataSources.wiki || {};
    const spaces = wiki.spaces || {};
    const spaceObj = spaces[space] || {};

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        wiki: {
          ...wiki,
          spaces: {
            ...spaces,
            [space]: {
              ...spaceObj,
              enabled: !spaceObj.enabled
            }
          }
        }
      }
    });
  };

  const handleWikiPageToggle = (space, pageName) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const wiki = dataSources.wiki || {};
    const spaces = wiki.spaces || {};
    const spaceObj = spaces[space] || {};
    const pages = spaceObj.pages || [];

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        wiki: {
          ...wiki,
          spaces: {
            ...spaces,
            [space]: {
              ...spaceObj,
              pages: pages.map(page =>
                page.name === pageName ? { ...page, enabled: !page.enabled } : page
              )
            }
          }
        }
      }
    });
  };

  const handleLocalDirectoryToggle = (dir) => {
    // Ensure dataSources and nested properties exist
    const dataSources = tempSettings.dataSources || {};
    const localFiles = dataSources.localFiles || {};
    const directories = localFiles.directories || {};
    const dirObj = directories[dir] || {};

    setTempSettings({
      ...tempSettings,
      dataSources: {
        ...dataSources,
        localFiles: {
          ...localFiles,
          directories: {
            ...directories,
            [dir]: {
              ...dirObj,
              enabled: !dirObj.enabled
            }
          }
        }
      }
    });
  };

  const handleUIPreferenceChange = (key, value) => {
    // Update only the temporary settings
    setTempSettings({
      ...tempSettings,
      [key]: value
    });
    
    // Handle theme change in UI immediately (but don't persist to context yet)
    if (key === 'theme') {
      document.querySelector("html").setAttribute("data-theme", value);
    }
  };

  const handleMemoriesCategoryChange = (value) => {
    setMemoriesCategory(value);
    setMemoriesOffset(0);
    loadMemories(0, value);
  };

  const handleMemoryEdit = (memory) => {
    setEditingMemoryId(memory.id);
    setEditingContent(memory.content);
    setEditingCategory(memory.category || 'other');
    setEditingImportance(memory.importance || 'medium');
  };

  const handleMemoryEditCancel = () => {
    setEditingMemoryId(null);
    setEditingContent('');
    setEditingCategory('');
    setEditingImportance('');
  };

  const handleMemoryEditSave = async (memoryId) => {
    setMemoryActionLoading(memoryId);
    try {
      await updateMemory(memoryId, {
        content: editingContent,
        category: editingCategory,
        importance: editingImportance,
      });
      setEditingMemoryId(null);
      await loadMemories(memoriesOffset, memoriesCategory);
    } catch (err) {
      console.error('Error updating memory:', err);
      setMemoriesError(err.message || 'Failed to update memory');
    } finally {
      setMemoryActionLoading(null);
    }
  };

  const handleMemoryDelete = async (memoryId) => {
    setMemoryActionLoading(memoryId);
    try {
      await deleteMemory(memoryId);
      // If we deleted the last item on this page, go to previous page
      if (memories.length === 1 && memoriesOffset > 0) {
        await loadMemories(memoriesOffset - MEMORIES_LIMIT, memoriesCategory);
      } else {
        await loadMemories(memoriesOffset, memoriesCategory);
      }
    } catch (err) {
      console.error('Error deleting memory:', err);
      setMemoriesError(err.message || 'Failed to delete memory');
    } finally {
      setMemoryActionLoading(null);
    }
  };

  const handleDeleteAllMemories = async () => {
    setShowDeleteAllConfirm(false);
    setMemoriesLoading(true);
    try {
      await deleteAllMemories();
      setMemories([]);
      setMemoriesTotal(0);
      setMemoriesOffset(0);
    } catch (err) {
      console.error('Error deleting all memories:', err);
      setMemoriesError(err.message || 'Failed to delete all memories');
    } finally {
      setMemoriesLoading(false);
    }
  };

  const handleMemoriesPageChange = (newOffset) => {
    loadMemories(newOffset, memoriesCategory);
  };

  const handleAddMemoryToggle = () => {
    setShowAddMemoryForm(!showAddMemoryForm);
    setNewMemoryContent('');
    setNewMemoryCategory('');
    setNewMemoryImportance('medium');
  };

  const handleAddMemorySave = async () => {
    if (!newMemoryContent.trim() || !newMemoryCategory) return;
    setAddMemoryLoading(true);
    try {
      await createMemory({
        content: newMemoryContent.trim(),
        category: newMemoryCategory,
        importance: newMemoryImportance,
      });
      setShowAddMemoryForm(false);
      setNewMemoryContent('');
      setNewMemoryCategory('');
      setNewMemoryImportance('medium');
      await loadMemories(0, memoriesCategory);
    } catch (err) {
      console.error('Error creating memory:', err);
      setMemoriesError(err.message || 'Failed to create memory');
    } finally {
      setAddMemoryLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getCategoryLabel = (value) => {
    const cat = MEMORY_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value || 'Other';
  };

  // Build a clean payload that only includes the pieces we persist
  const buildSettingsPayload = (raw) => {
    return {
      // High-level mode (chat | agent | assistant)
      mode: raw.mode || 'chat',

      // Chat-specific choices
      chat: {
        selectedLLM: raw.chat?.selectedLLM || 'gpt-5.6-sol',
        llms: [...DEFAULT_LLMS], // Always use frontend-defined LLMs
      },

      // Agent-specific choices
      agent: {
        selectedTools: raw.agent?.selectedTools || ['docs', 'workfront', 'slack', 'memory'],
        tools: raw.agent?.tools || ['docs', 'workfront', 'slack', 'memory'],
      },

      // Assistant-specific choices (doc assistant)
      assistant: {
        selectedSolutions: raw.assistant?.selectedSolutions || [],
        solutions: raw.assistant?.solutions || [],
      },

      // General UI / profile
      theme: raw.theme || 'dark',
      responseConfig: raw.responseConfig || { tone: 'professional', responseLength: 'balanced' },
      userProfile: raw.userProfile || { preferredName: '', jobTitle: '', organization: '', team: '', preferredLanguage: 'English' },

      // Advanced
      customPrompts: raw.customPrompts || [],
      dataSources: raw.dataSources || {},
    };
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const payload = buildSettingsPayload(tempSettings);
      // Update the actual settings in context - now async
      const success = await updateSettings(payload);
      
      if (success) {
        setHasChanges(false);
      } else {
        setSaveError("Failed to save settings. Please try again.");
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      setSaveError("An unexpected error occurred while saving settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowUnsavedAlert(true);
    } else {
      closeModal();
    }
  };

  const closeModal = () => {
    // If theme was changed but not saved, revert the visual change
    if (tempSettings.theme !== settings.theme) {
      document.querySelector("html").setAttribute("data-theme", settings.theme);
    }
    setShowUnsavedAlert(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedAlert(false);
  };

  // Get the current theme from tempSettings
  const currentTheme = tempSettings.theme;

  return (
    <div className="settings-modal-overlay" onClick={handleClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <button className="settings-close-button" onClick={handleClose}>
          <FiX />
        </button>
        
        <div className="settings-container">
          
          <div className="settings-sidebar">
            <button 
              className={`settings-tab ${activeTab === 'ui' ? 'active' : ''}`}
              onClick={() => setActiveTab('ui')}
            >
              <FiSettings /> UI Preferences
            </button>
            <button 
              className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <FiUser /> User Profile
            </button>
            <button 
              className={`settings-tab ${activeTab === 'response' ? 'active' : ''}`}
              onClick={() => setActiveTab('response')}
            >
              <FiMessageSquare /> Response Configuration
            </button>
            <button 
              className={`settings-tab ${activeTab === 'prompts' ? 'active' : ''}`}
              onClick={() => setActiveTab('prompts')}
            >
              <FiCommand /> Custom Prompts
            </button>
            <button 
              className={`settings-tab ${activeTab === 'memories' ? 'active' : ''}`}
              onClick={() => setActiveTab('memories')}
            >
              <FiCpu /> Memories
            </button>
            <button 
              className={`settings-tab ${activeTab === 'sources' ? 'active' : ''}`}
              onClick={() => setActiveTab('sources')}
              style={{ display: 'none' }}
            >
              <FiDatabase /> Data Sources
            </button>
            
            <button className="settings-tab logout-button" onClick={handleLogout}>
              <FiLogOut /> Logout
            </button>
          </div>

          <div className="settings-content">
            {error && (
              <AlertBox
                type="error"
                message={error}
              />
            )}
            
            {loading && !isSaving && (
              <AlertBox
                type="info"
                message="Loading settings..."
              />
            )}
            
            {saveError && (
              <AlertBox
                type="error"
                message={saveError}
                onDismiss={() => setSaveError(null)}
              />
            )}
          
            {showUnsavedAlert && (
              <AlertBox 
                type="info"
                message="You have unsaved changes. Are you sure you want to close without saving?"
                actions={
                  <>
                    <button onClick={handleCancelClose}>Cancel</button>
                    <button onClick={closeModal}>OK</button>
                  </>
                }
              />
            )}
            
            {activeTab === 'ui' && (
              <div className="settings-section">
                <h2>UI Preferences</h2>
                <div className="settings-group">
                  <label>
                    Theme
                    <CustomSelect
                      value={currentTheme}
                      onChange={(value) => handleUIPreferenceChange('theme', value)}
                      options={['light', 'dark']}
                    />
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="settings-section">
                <h2>User Profile</h2>
                
                <div className="settings-group">
                  <label>
                    Preferred Name
                    <input 
                      type="text"
                      value={tempSettings.userProfile?.preferredName || ''}
                      onChange={(e) => handleUserProfileChange('preferredName', e.target.value)}
                      placeholder="How should we address you?"
                    />
                  </label>

                  <label>
                    Job Title
                    <input 
                      type="text"
                      value={tempSettings.userProfile?.jobTitle || ''}
                      onChange={(e) => handleUserProfileChange('jobTitle', e.target.value)}
                      placeholder="Your role in the organization"
                    />
                  </label>

                  <label>
                    Organization
                    <input 
                      type="text"
                      value={tempSettings.userProfile?.organization || ''}
                      onChange={(e) => handleUserProfileChange('organization', e.target.value)}
                      placeholder="Your organization"
                    />
                  </label>

                  <label>
                    Team
                    <input 
                      type="text"
                      value={tempSettings.userProfile?.team || ''}
                      onChange={(e) => handleUserProfileChange('team', e.target.value)}
                      placeholder="Your team"
                    />
                  </label>

                  <label>
                    Preferred Language
                    <input 
                      type="text"
                      value={tempSettings.userProfile?.preferredLanguage || ''}
                      onChange={(e) => handleUserProfileChange('preferredLanguage', e.target.value)}
                      placeholder="Your preferred language"
                    />
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'response' && (
              <div className="settings-section">
                <h2>Response Configuration</h2>
                
                <div className="settings-group">
                  <label>
                    Tone
                    <CustomSelect
                      value={tempSettings.responseConfig.tone}
                      onChange={(value) => handleResponseConfigChange('tone', value)}
                      options={['professional', 'friendly', 'technical']}
                    />
                  </label>

                  <label>
                    Response Length
                    <CustomSelect
                      value={tempSettings.responseConfig.responseLength}
                      onChange={(value) => handleResponseConfigChange('responseLength', value)}
                      options={['concise', 'balanced', 'detailed']}
                    />
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'prompts' && (
              <div className="settings-section">
                <h2>Custom Prompts</h2>
                <p className="section-description">
                  Use System Prompts to change the behavior of COMPAS for all queries. For example, you might ask COMPAS to not greet you, respond in a specific format or style, or include any other information that will make COMPAS more personal to you. These prompts will be applied to every conversation.
                </p>
                
                <div className="settings-group">
                  <div className="custom-prompts-list">
                    {Array.isArray(tempSettings.customPrompts) ? tempSettings.customPrompts.map((prompt, index) => (
                      <div key={index} className="custom-prompt-item">
                        <p>{prompt}</p>
                        <button onClick={() => handleCustomPromptDelete(index)}>Delete</button>
                      </div>
                    )) : null}
                  </div>
                  <div className="add-prompt-form">
                    <textarea 
                      placeholder="Enter a new custom prompt"
                      rows="4"
                      id="new-prompt-textarea"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          const textarea = e.target;
                          if (textarea.value.trim()) {
                            handleCustomPromptAdd(textarea.value.trim());
                            textarea.value = '';
                          }
                          e.preventDefault();
                        }
                      }}
                    ></textarea>
                    <div className="prompt-form-footer">
                      <span className="keyboard-hint">Press Ctrl+Enter to add</span>
                      <button 
                        className="add-prompt-button"
                        onClick={() => {
                          const textarea = document.getElementById('new-prompt-textarea');
                          if (textarea.value.trim()) {
                            handleCustomPromptAdd(textarea.value.trim());
                            textarea.value = '';
                          }
                        }}
                      >
                        Add Prompt
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'memories' && (
              <div className="settings-section">
                <h2>Memories</h2>
                <p className="section-description">
                  COMPAS remembers things you share during conversations to personalize your experience. 
                  You can also say "remember this" in a conversation to save something specific. View, add, edit, or delete any memory here.
                </p>

                {memoriesError && (
                  <AlertBox
                    type="error"
                    message={memoriesError}
                    onDismiss={() => setMemoriesError(null)}
                  />
                )}

                {showDeleteAllConfirm && (
                  <AlertBox
                    type="warning"
                    message={`Are you sure you want to delete all ${memoriesTotal} memories? This action cannot be undone.`}
                    actions={
                      <>
                        <button onClick={() => setShowDeleteAllConfirm(false)}>Cancel</button>
                        <button onClick={handleDeleteAllMemories} className="memories-delete-confirm-btn">Delete All</button>
                      </>
                    }
                  />
                )}

                <div className="memories-toolbar">
                  <div className="memories-filter">
                    <label>Filter by category</label>
                    <CustomSelect
                      value={memoriesCategory}
                      onChange={handleMemoriesCategoryChange}
                      options={MEMORY_CATEGORIES.map(c => c.value)}
                      labels={MEMORY_CATEGORIES.reduce((acc, c) => { acc[c.value] = c.label; return acc; }, {})}
                    />
                  </div>
                  <div className="memories-toolbar-actions">
                    <button 
                      className="memories-add-button"
                      onClick={handleAddMemoryToggle}
                      disabled={memoriesLoading}
                    >
                      <FiPlus /> Add Memory
                    </button>
                    {memoriesTotal > 0 && (
                      <button 
                        className="memories-delete-all-button"
                        onClick={() => setShowDeleteAllConfirm(true)}
                        disabled={memoriesLoading}
                      >
                        <FiTrash2 /> Delete All
                      </button>
                    )}
                  </div>
                </div>

                {showAddMemoryForm && (
                  <div className="memory-add-form">
                    <textarea
                      className="memory-edit-content"
                      value={newMemoryContent}
                      onChange={(e) => setNewMemoryContent(e.target.value)}
                      placeholder="What would you like COMPAS to remember?"
                      rows={3}
                      autoFocus
                    />
                    <div className="memory-edit-fields">
                      <label>
                        Category
                        <select
                          value={newMemoryCategory}
                          onChange={(e) => setNewMemoryCategory(e.target.value)}
                          className={`memory-edit-select ${!newMemoryCategory ? 'placeholder' : ''}`}
                        >
                          <option value="" disabled>Select a category...</option>
                          {MEMORY_CATEGORIES.filter(c => c.value).map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Importance
                        <select
                          value={newMemoryImportance}
                          onChange={(e) => setNewMemoryImportance(e.target.value)}
                          className="memory-edit-select"
                        >
                          {IMPORTANCE_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="memory-edit-actions">
                      <button 
                        className="memory-save-btn"
                        onClick={handleAddMemorySave}
                        disabled={addMemoryLoading || !newMemoryContent.trim() || !newMemoryCategory}
                      >
                        <FiCheck /> {addMemoryLoading ? 'Adding...' : 'Add'}
                      </button>
                      <button 
                        className="memory-cancel-btn"
                        onClick={handleAddMemoryToggle}
                        disabled={addMemoryLoading}
                      >
                        <FiXCircle /> Cancel
                      </button>
                    </div>
                  </div>
                )}

                {memoriesLoading && memories.length === 0 ? (
                  <div className="memories-loading">Loading memories...</div>
                ) : memories.length === 0 ? (
                  <div className="memories-empty">
                    <p>No memories found{memoriesCategory ? ` in "${getCategoryLabel(memoriesCategory)}"` : ''}.</p>
                    <p className="memories-empty-hint">Memories are automatically created as you chat, or you can say "remember this" in a conversation.</p>
                  </div>
                ) : (
                  <>
                    <div className="memories-count">
                      Showing {memoriesOffset + 1}–{Math.min(memoriesOffset + MEMORIES_LIMIT, memoriesTotal)} of {memoriesTotal} memories
                    </div>
                    <div className="memories-list">
                      {memories.map((memory) => (
                        <div key={memory.id} className={`memory-card ${editingMemoryId === memory.id ? 'editing' : ''}`}>
                          {editingMemoryId === memory.id ? (
                            <div className="memory-edit-form">
                              <textarea
                                className="memory-edit-content"
                                value={editingContent}
                                onChange={(e) => setEditingContent(e.target.value)}
                                rows={3}
                              />
                              <div className="memory-edit-fields">
                                <label>
                                  Category
                                  <select
                                    value={editingCategory}
                                    onChange={(e) => setEditingCategory(e.target.value)}
                                    className="memory-edit-select"
                                  >
                                    {MEMORY_CATEGORIES.filter(c => c.value).map(c => (
                                      <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  Importance
                                  <select
                                    value={editingImportance}
                                    onChange={(e) => setEditingImportance(e.target.value)}
                                    className="memory-edit-select"
                                  >
                                    {IMPORTANCE_OPTIONS.map(opt => (
                                      <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              <div className="memory-edit-actions">
                                <button 
                                  className="memory-save-btn"
                                  onClick={() => handleMemoryEditSave(memory.id)}
                                  disabled={memoryActionLoading === memory.id || !editingContent.trim()}
                                >
                                  <FiCheck /> {memoryActionLoading === memory.id ? 'Saving...' : 'Save'}
                                </button>
                                <button 
                                  className="memory-cancel-btn"
                                  onClick={handleMemoryEditCancel}
                                  disabled={memoryActionLoading === memory.id}
                                >
                                  <FiXCircle /> Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="memory-content">{memory.content}</div>
                              <div className="memory-meta">
                                <span className={`memory-badge importance-${memory.importance || 'medium'}`}>
                                  {(memory.importance || 'medium').charAt(0).toUpperCase() + (memory.importance || 'medium').slice(1)}
                                </span>
                                <span className="memory-badge category-badge">
                                  {getCategoryLabel(memory.category)}
                                </span>
                                <span className="memory-date">
                                  {formatDate(memory.updatedAt || memory.createdAt)}
                                </span>
                              </div>
                              <div className="memory-actions">
                                <button 
                                  className="memory-edit-btn"
                                  onClick={() => handleMemoryEdit(memory)}
                                  disabled={memoryActionLoading === memory.id}
                                  title="Edit memory"
                                >
                                  <FiEdit2 />
                                </button>
                                <button 
                                  className="memory-delete-btn"
                                  onClick={() => handleMemoryDelete(memory.id)}
                                  disabled={memoryActionLoading === memory.id}
                                  title="Delete memory"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>

                    {memoriesTotal > MEMORIES_LIMIT && (
                      <div className="memories-pagination">
                        <button
                          className="memories-page-btn"
                          disabled={memoriesOffset === 0 || memoriesLoading}
                          onClick={() => handleMemoriesPageChange(memoriesOffset - MEMORIES_LIMIT)}
                        >
                          Previous
                        </button>
                        <span className="memories-page-info">
                          Page {Math.floor(memoriesOffset / MEMORIES_LIMIT) + 1} of {Math.ceil(memoriesTotal / MEMORIES_LIMIT)}
                        </span>
                        <button
                          className="memories-page-btn"
                          disabled={memoriesOffset + MEMORIES_LIMIT >= memoriesTotal || memoriesLoading}
                          onClick={() => handleMemoriesPageChange(memoriesOffset + MEMORIES_LIMIT)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'sources' && (
              <div className="settings-section" style={{ display: 'none' }}>
                <h2>Data Sources</h2>
                
                <AlertBox
                  type="warning"
                  message="This feature is currently in development. The ability to connect and use external data sources will be available in a future update."
                />
                
                <div className="settings-group">
                  <div className="data-sources-grid">
                    <div className="data-source-item">
                      <div className="data-source-header">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox"
                            checked={tempSettings.dataSources?.documentation?.enabled || false}
                            onChange={() => handleDataSourceToggle('documentation')}
                          />
                          Documentation
                        </label>
                      </div>
                      {tempSettings.dataSources?.documentation?.enabled && (
                        <div className="data-source-content">
                          {tempSettings.dataSources?.documentation?.solutions && Object.entries(tempSettings.dataSources.documentation.solutions).map(([key, solution]) => (
                            <div key={key} className="data-source-solution">
                              <label className="checkbox-label">
                                <input 
                                  type="checkbox"
                                  checked={solution?.enabled || false}
                                  onChange={() => handleSolutionToggle('documentation', key)}
                                />
                                {solution?.name || key}
                              </label>
                              {solution?.enabled && (
                                <div className="data-source-sections">
                                  {Array.isArray(solution.sections) ? solution.sections.map((section, idx) => (
                                    <label key={idx} className="checkbox-label section-label">
                                      <input 
                                        type="checkbox"
                                        checked={section?.enabled || false}
                                        onChange={() => handleSectionToggle('documentation', key, section.name)}
                                      />
                                      {section?.name || ''}
                                    </label>
                                  )) : null}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="data-source-item">
                      <div className="data-source-header">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox"
                            checked={tempSettings.dataSources?.workfront?.enabled || false}
                            onChange={() => handleDataSourceToggle('workfront')}
                          />
                          Workfront
                        </label>
                      </div>
                      {tempSettings.dataSources?.workfront?.enabled && (
                        <div className="data-source-content">
                          {tempSettings.dataSources?.workfront?.sections && Object.entries(tempSettings.dataSources.workfront.sections).map(([key, section]) => (
                            <div key={key} className="data-source-section">
                              <label className="checkbox-label">
                                <input 
                                  type="checkbox"
                                  checked={section?.enabled || false}
                                  onChange={() => handleWorkfrontSectionToggle(key)}
                                />
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </label>
                              {section?.enabled && (
                                <div className="data-source-filters">
                                  {Array.isArray(section.filters) ? section.filters.map((filter, idx) => (
                                    <label key={idx} className="checkbox-label filter-label">
                                      <input 
                                        type="checkbox"
                                        checked={filter?.enabled || false}
                                        onChange={() => handleWorkfrontFilterToggle(key, filter.name)}
                                      />
                                      {filter?.name || ''}
                                    </label>
                                  )) : null}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="data-source-item">
                      <div className="data-source-header">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox"
                            checked={tempSettings.dataSources?.slack?.enabled || false}
                            onChange={() => handleDataSourceToggle('slack')}
                          />
                          Slack
                        </label>
                      </div>
                      {tempSettings.dataSources?.slack?.enabled && (
                        <div className="data-source-content">
                          {tempSettings.dataSources?.slack?.channels && Object.entries(tempSettings.dataSources.slack.channels).map(([key, category]) => (
                            <div key={key} className="data-source-category">
                              <label className="checkbox-label">
                                <input 
                                  type="checkbox"
                                  checked={category?.enabled || false}
                                  onChange={() => handleSlackCategoryToggle(key)}
                                />
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </label>
                              {category?.enabled && (
                                <div className="data-source-channels">
                                  {Array.isArray(category.channels) ? category.channels.map((channel, idx) => (
                                    <label key={idx} className="checkbox-label channel-label">
                                      <input 
                                        type="checkbox"
                                        checked={channel?.enabled || false}
                                        onChange={() => handleSlackChannelToggle(key, channel.name)}
                                      />
                                      {channel?.name || ''}
                                    </label>
                                  )) : null}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="data-source-item">
                      <div className="data-source-header">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox"
                            checked={tempSettings.dataSources?.wiki?.enabled || false}
                            onChange={() => handleDataSourceToggle('wiki')}
                          />
                          Wiki
                        </label>
                      </div>
                      {tempSettings.dataSources?.wiki?.enabled && (
                        <div className="data-source-content">
                          {tempSettings.dataSources?.wiki?.spaces && Object.entries(tempSettings.dataSources.wiki.spaces).map(([key, space]) => (
                            <div key={key} className="data-source-space">
                              <label className="checkbox-label">
                                <input 
                                  type="checkbox"
                                  checked={space?.enabled || false}
                                  onChange={() => handleWikiSpaceToggle(key)}
                                />
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </label>
                              {space?.enabled && (
                                <div className="data-source-pages">
                                  {Array.isArray(space.pages) ? space.pages.map((page, idx) => (
                                    <label key={idx} className="checkbox-label page-label">
                                      <input 
                                        type="checkbox"
                                        checked={page?.enabled || false}
                                        onChange={() => handleWikiPageToggle(key, page.name)}
                                      />
                                      {page?.name || ''}
                                    </label>
                                  )) : null}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="data-source-item">
                      <div className="data-source-header">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox"
                            checked={tempSettings.dataSources?.localFiles?.enabled || false}
                            onChange={() => handleDataSourceToggle('localFiles')}
                          />
                          Local Files
                        </label>
                      </div>
                      {tempSettings.dataSources?.localFiles?.enabled && (
                        <div className="data-source-content">
                          {tempSettings.dataSources?.localFiles?.directories && Object.entries(tempSettings.dataSources.localFiles.directories).map(([key, dir]) => (
                            <div key={key} className="data-source-directory">
                              <label className="checkbox-label">
                                <input 
                                  type="checkbox"
                                  checked={dir?.enabled || false}
                                  onChange={() => handleLocalDirectoryToggle(key)}
                                />
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </label>
                              {dir?.enabled && (
                                <div className="directory-details">
                                  <span className="directory-path">{dir.path || ''}</span>
                                  <span className="directory-description">{dir.description || ''}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {hasChanges && (
          <div className="settings-save-bar">
            <button 
              className="settings-save-button" 
              onClick={handleSaveChanges}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings; 